import React from 'react';
import { MapPin, Fuel, Zap, Wrench, Coffee, BedDouble, ExternalLink, Search, Navigation, Star } from 'lucide-react';

const CATEGORIES = [
  { id: 'gas',       label: 'Gas Stations',    icon: Fuel,      q: 'gas stations' },
  { id: 'ev',        label: 'EV Charging',     icon: Zap,       q: 'ev charging stations' },
  { id: 'service',   label: 'Service Stations', icon: Wrench,    q: 'auto service and repair' },
  { id: 'food',      label: 'Food & Coffee',   icon: Coffee,    q: 'restaurants and coffee shops' },
  { id: 'rest',      label: 'Rest Stops',      icon: BedDouble, q: 'rest stops and hotels' },
];

const POPULAR_CITIES = ['Toronto, ON', 'Vancouver, BC', 'Calgary, AB', 'Montreal, QC', 'Ottawa, ON', 'Halifax, NS'];

const gmapsSearchUrl = (q, where) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${q} near ${where}`)}`;

const gmapsEmbedUrl = (q, where) =>
  `https://www.google.com/maps?q=${encodeURIComponent(`${q} near ${where}`)}&output=embed&z=12`;

const gmapsDirectionsUrl = (dest) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;

export default function Maps() {
  const [cat, setCat] = React.useState('gas');
  const [where, setWhere] = React.useState('Toronto, ON');
  const [input, setInput] = React.useState('Toronto, ON');
  const [locating, setLocating] = React.useState(false);

  const active = CATEGORIES.find((c) => c.id === cat);

  const applyLocation = (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    setWhere(input.trim());
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`;
        setInput(c);
        setWhere(c);
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  return (
    <div data-testid="page-maps">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-12 pb-8">
        <div className="grid lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Maps &amp; Amenities</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">
              Every stop on the way there.
            </h1>
            <p className="mt-3 text-zinc-400 text-sm max-w-lg">
              Find gas stations, EV chargers, service shops, food, and rest stops along your route. Every result opens straight in Google Maps.
            </p>
          </div>
          <form onSubmit={applyLocation} className="lg:col-span-5 flex flex-col sm:flex-row gap-2" data-testid="maps-location-form">
            <div className="relative flex-1">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="City, address, or coordinates"
                className="w-full bg-[#0d0d0d] hairline pl-9 pr-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
                data-testid="maps-location-input"
              />
            </div>
            <button
              type="submit"
              className="hairline hover:border-[#ff2a2a] text-white text-sm font-semibold px-4 py-2.5 transition-colors inline-flex items-center gap-1.5"
              data-testid="maps-search"
            >
              <Search className="w-4 h-4" /> Search
            </button>
            <button
              type="button"
              onClick={useMyLocation}
              disabled={locating}
              className="bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 transition-colors inline-flex items-center gap-1.5"
              data-testid="maps-use-location"
            >
              <Navigation className="w-4 h-4" /> {locating ? 'Locating…' : 'Use my location'}
            </button>
          </form>
        </div>

        {/* Popular cities pill row */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="popular-cities">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 self-center mr-1">Popular:</span>
          {POPULAR_CITIES.map((c) => (
            <button
              key={c}
              onClick={() => { setInput(c); setWhere(c); }}
              className={`text-xs font-mono uppercase tracking-wide px-2.5 py-1.5 transition-colors ${
                where === c ? 'bg-[#ff2a2a] text-white' : 'hairline text-zinc-400 hover:text-white'
              }`}
              data-testid={`city-${c.split(',')[0].toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Category chips */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap gap-2" data-testid="category-chips">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-2 transition-colors ${
                cat === c.id ? 'bg-[#ff2a2a] text-white' : 'hairline text-zinc-400 hover:text-white'
              }`}
              data-testid={`cat-${c.id}`}
            >
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Map + side rail */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-6 pb-14">
        <div className="grid lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8 am-card overflow-hidden" data-testid="maps-embed">
            <iframe
              key={`${cat}-${where}`}
              title={`${active.label} near ${where}`}
              src={gmapsEmbedUrl(active.q, where)}
              className="w-full h-[560px] block"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <aside className="lg:col-span-4 am-card p-5" data-testid="maps-actions">
            <div className="flex items-start gap-3">
              <active.icon className="w-6 h-6 text-[#ff2a2a] shrink-0" />
              <div className="min-w-0">
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Showing</div>
                <div className="font-display text-xl uppercase tracking-tight">{active.label}</div>
                <div className="text-xs text-zinc-400 mt-0.5">near <span className="text-white">{where}</span></div>
              </div>
            </div>

            <a
              href={gmapsSearchUrl(active.q, where)}
              target="_blank"
              rel="noreferrer"
              className="mt-5 w-full bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold py-3 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="maps-open-google"
            >
              <ExternalLink className="w-4 h-4" /> Open in Google Maps
            </a>

            <div className="mt-6 hairline-t pt-5 space-y-2">
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Quick jumps</div>
              {CATEGORIES.filter((c) => c.id !== cat).map((c) => (
                <a
                  key={c.id}
                  href={gmapsSearchUrl(c.q, where)}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full inline-flex items-center gap-2 text-sm text-zinc-200 hover:text-[#ff2a2a] transition-colors py-2"
                  data-testid={`quick-${c.id}`}
                >
                  <c.icon className="w-4 h-4" /> {c.label} near {where.split(',')[0]}
                  <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
                </a>
              ))}
            </div>

            <div className="mt-6 hairline-t pt-5">
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">Plan a route</div>
              <a
                href={gmapsDirectionsUrl(`${active.q} near ${where}`)}
                target="_blank"
                rel="noreferrer"
                className="w-full hairline hover:border-[#ff2a2a] text-white text-sm font-semibold py-2.5 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="maps-directions"
              >
                <Navigation className="w-4 h-4" /> Get directions
              </a>
            </div>
          </aside>
        </div>

        {/* Helpful tips row */}
        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="maps-tips">
          {[
            { icon: Star, title: 'Trip-ready',      text: 'Save stops from Google Maps to your account so they sync across every device.' },
            { icon: Zap,  title: 'EV routing',      text: 'Google Maps auto-plots charging stops for your car\u2019s range on longer routes.' },
            { icon: Wrench, title: 'Mid-trip help', text: 'One tap to nearest service if something goes wrong on the road.' },
          ].map((t, i) => (
            <div key={i} className="am-card p-5">
              <t.icon className="w-5 h-5 text-[#ff2a2a]" />
              <div className="mt-3 font-display text-lg uppercase tracking-tight">{t.title}</div>
              <div className="mt-1 text-sm text-zinc-400">{t.text}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
